/**
 * Overview fleet + KPI poll helpers (FE-5a) — extracted from overview.route.js.
 */

import { _sleep } from "./api.js";
import { OFFLINE_MS } from "./constants.js";
import { pickDevicesPollPath, pickOverviewPollPath } from "./device-presence.js";
import { FLEET_POLL_MAX_PAGES, fetchFleetDevicesPoll, fleetPollTruncatedHtml } from "./fleet-list-paginated.js";
import { hasUsableOverviewGroupSlots, resolveOverviewFleetMaxPages } from "./overview-group-slots.js";

export const OVERVIEW_LIVE_MS = 10000;

/**
 * @param {object[]} devices
 * @returns {number}
 */
export function countOverviewOnlineDevices(devices) {
  const arr = Array.isArray(devices) ? devices : [];
  return arr.filter((d) => {
    if (typeof d.is_online === "boolean") return d.is_online;
    return Date.now() - Date.parse(d.updated_at || 0) < OFFLINE_MS;
  }).length;
}

/**
 * @param {number} v
 * @returns {string}
 */
export function formatOverviewBps(v) {
  const n = Number(v || 0);
  if (n < 1024) return `${n.toFixed(0)} B/s`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB/s`;
  return `${(n / 1024 / 1024).toFixed(2)} MB/s`;
}

/**
 * @param {object | null | undefined} ov
 * @param {object[]} devices
 * @param {{ health?: object, isSuper?: boolean }} [ctx]
 * @returns {{ server: string, devices: number, online: number, offline: number, tx: string, rx: string, queue: number, dropped: number, risk: string, riskClass: string }}
 */
export function buildOverviewKpiPatch(ov, devices, ctx = {}) {
  const o = ov && typeof ov === "object" ? ov : {};
  const devs = Array.isArray(devices) ? devices : [];
  const hh = ctx.health && typeof ctx.health === "object" ? ctx.health : {};
  const isSuper = !!ctx.isSuper;
  const httpOk = !!(hh.ok ?? true);
  const mqRaw = hh.mqtt_connected ?? o.mqtt_connected;
  const mqConnected = mqRaw === true;
  const mqUnknown = mqRaw !== true && mqRaw !== false;
  const mqQDepth = Number(hh.mqtt_ingest_queue_depth || 0);
  const mqDropped = Number(hh.mqtt_ingest_dropped || 0);
  const totalDevices = Number(o.total_devices != null ? o.total_devices : devs.length);
  const onlineDevices = Number(
    (o.presence && o.presence.online != null) ? o.presence.online : countOverviewOnlineDevices(devs),
  );
  const offlineDevices = Math.max(0, totalDevices - onlineDevices);
  const txBps = Number((o.throughput && o.throughput.tx_bps_total) || 0);
  const rxBps = Number((o.throughput && o.throughput.rx_bps_total) || 0);
  const mqStatus = mqUnknown ? "Unknown" : !mqConnected ? "Disconnected" : (mqDropped > 0 || mqQDepth >= 300 ? "Warning" : "Healthy");
  const mqClass = mqUnknown ? "offline" : !mqConnected ? "revoked" : (mqStatus === "Warning" ? "offline" : "online");
  const mqttLbl = mqUnknown ? "Link —" : (mqConnected ? "Link up" : "Link down");
  const server = isSuper
    ? `${httpOk ? "API ok" : "API down"} · ${mqttLbl}`
    : (httpOk && mqConnected ? "Status ok" : "Status down");
  return {
    server,
    devices: totalDevices,
    online: onlineDevices,
    offline: offlineDevices,
    tx: formatOverviewBps(txBps),
    rx: formatOverviewBps(rxBps),
    queue: mqQDepth,
    dropped: mqDropped,
    risk: mqStatus,
    riskClass: mqClass,
  };
}

/**
 * @param {object | null | undefined} ov
 * @param {number | null | undefined} override
 * @returns {number}
 */
function _fleetPagesForOverview(ov, override) {
  const resolved = resolveOverviewFleetMaxPages(ov, override);
  const n = Number.isFinite(Number(resolved)) ? Number(resolved) : FLEET_POLL_MAX_PAGES;
  // KILL-MERGE: overview poll never walks > FLEET_POLL_MAX_PAGES (1).
  if (n <= 0) return 0;
  return Math.min(Math.floor(n), FLEET_POLL_MAX_PAGES);
}

/**
 * Initial overview + fleet load with one retry (overview-first for fleet cap).
 *
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number, retries?: number, maxFleetPages?: number }} [opts]
 * @returns {Promise<{ ov: object | null, list: object | null }>}
 */
export async function fetchOverviewAndDevicesBundle(apiFn, opts = {}) {
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 22000;
  const retries = Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 3;
  let bestOv = null;
  let bestList = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const ovVal = await apiFn(pickOverviewPollPath(), { timeoutMs, retries });
      if (ovVal && typeof ovVal === "object") bestOv = ovVal;
    } catch (_) { /* retry */ }
    if (bestOv) {
      const devPath = pickDevicesPollPath();
      const live = devPath.includes("skip_cache=1");
      const maxPages = _fleetPagesForOverview(bestOv, opts.maxFleetPages);
      try {
        bestList = await fetchFleetDevicesPoll({ live, timeoutMs, retries, maxPages });
      } catch (_) { /* retry */ }
    }
    if (bestOv && bestList) return { ov: bestOv, list: bestList };
    if (attempt === 0) await _sleep(500);
  }
  return { ov: bestOv, list: bestList };
}

/**
 * Single live poll tick (overview + optional capped fleet).
 *
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number, retries?: number, maxFleetPages?: number, beforeFetch?: () => void }} [opts]
 * @returns {Promise<{ ov: object | null, list: object | null }>}
 */
export async function fetchOverviewLivePair(apiFn, opts = {}) {
  if (typeof opts.beforeFetch === "function") {
    try {
      opts.beforeFetch();
    } catch (_) {}
  }
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 20000;
  const retries = Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 2;
  let ovN = null;
  try {
    const raw = await apiFn(pickOverviewPollPath(), { timeoutMs, retries });
    ovN = raw && typeof raw === "object" ? raw : null;
  } catch (_) {
    ovN = null;
  }
  const maxPages = _fleetPagesForOverview(ovN, opts.maxFleetPages);
  let listN = null;
  if (maxPages > 0) {
    const devPathLive = pickDevicesPollPath();
    const live = devPathLive.includes("skip_cache=1");
    try {
      listN = await fetchFleetDevicesPoll({ live, timeoutMs, retries, maxPages });
    } catch (_) {
      listN = null;
    }
  }
  return { ov: ovN, list: listN };
}

/**
 * Fleet trunc banner on overview — hidden when server group slots drive cards (CAP-API-1a-3).
 *
 * @param {object | null | undefined} list
 * @param {object | null | undefined} ov
 * @param {string} [bannerId]
 * @returns {string}
 */
export function overviewFleetPollTruncatedHtml(list, ov, bannerId = "ovFleetTruncBanner") {
  if (hasUsableOverviewGroupSlots(ov)) return "";
  return fleetPollTruncatedHtml(list, bannerId);
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {object | null | undefined} list
 * @param {object | null | undefined} ov
 * @param {string} [bannerId]
 */
export function syncOverviewFleetPollTruncBanner(view, list, ov, bannerId = "ovFleetTruncBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  const html = overviewFleetPollTruncatedHtml(list, ov, bannerId);
  if (!html) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("header.page-head, section.card, .page-head");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}
